/**
 * FikoDFM Component - CATIA-style DFM Analysis Panel
 *
 * Ölçüm diyaloğu ile aynı stil: Kompakt toolbar, karanlık tema, Semantic UI
 */

import {
    Component,
    ChangeDetectorRef,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    OnInit,
    OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';

import {
    FikoDFMAnalyzer,
    getDFMAnalyzer,
    DFMReport,
    DFMIssue,
    DFMSeverity,
    ManufacturingProcess,
    GrupBrepResult,
    DFMCostParams
} from '../FikoBrep';
import { FikoCizimYalin } from '../FikoCizimYalin';

/** Seçili FikoCizimYalin bilgisi */
interface SeciliCizimBilgi {
    cizim: FikoCizimYalin;
    ad: string;
}

export type DFMViewMode = 'Sonuc' | 'Liste' | 'Maliyet' | 'Ayarlar';

export interface CustomCostItem {
    id: string;
    name: string;
    costPerPart: number;
    category: 'packaging' | 'shipping' | 'assembly' | 'testing' | 'certification' | 'other';
    enabled: boolean;
}

export interface DFMAyarlar {
    otomatikAnaliz: boolean;
    secilenProses: ManufacturingProcess;
    raporFormati: 'txt' | 'html' | 'json';
    gosterKritik: boolean;
    gosterUyari: boolean;
    gosterBilgi: boolean;
    gosterUygun: boolean;

    // Uretim parametreleri
    batchSize: number;
    annualVolume: number;
    profitMarginPercent: number;
    overheadPercent: number;
    qualityControlPercent: number;
    tightestToleranceMM: number;
    requiredSurfaceRa: number;
    secilenMalzeme: string;         // '' = otomatik
    paraBirimi: 'USD' | 'EUR' | 'TRY';

    // Custom maliyet kalemleri
    customCostItems: CustomCostItem[];
}

@Component({
    selector: 'fikoyt-dfm',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule
    ],
    templateUrl: './fiko-dfm.component.html',
    styleUrls: ['./fiko-dfm.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FikoDFMComponent implements OnInit, OnDestroy {
    @Input() anaGosterim: any; // FikoThreeJS reference
    @Input() pencere: any; // Dialog pencere referansı (FikoDialogService'den geliyor)

    @Output() analyzeRequested = new EventEmitter<void>();
    @Output() issueSelected = new EventEmitter<DFMIssue>();
    @Output() kapandiEvent = new EventEmitter<void>();

    // State
    dfmViewMode: DFMViewMode = 'Sonuc';
    isAnalyzing = false;
    currentReport: DFMReport | null = null;
    selectedIssue: DFMIssue | null = null;
    gpuMode: 'WEBGPU' | 'WASM' | 'CPU' = 'CPU';
    hataMesaji: string | null = null;

    // Progress göstergesi
    progressPercent = 0;
    progressMessage = '';

    // Yüzey vurgulama
    highlightedFeature: string | null = null;
    private _originalMaterials = new Map<any, any>(); // Orijinal materyalleri sakla
    private _highlightColor = 0xFFD700; // Altın sarısı
    private _currentBrepResult: GrupBrepResult | null = null; // Analiz sonucu BREP verisi

    // Seçili FikoCizimYalin listesi
    seciliCizimler: SeciliCizimBilgi[] = [];

    // ============ PARÇA SEÇİMİ (Kumpas/Cetvel benzeri subscription pattern) ============
    /** Seçili aktif parça (analiz edilecek) */
    seciliAktifParca: FikoCizimYalin | null = null;
    /** Parça seçme modu aktif mi */
    parcaSecModu: boolean = false;
    /** Seçim subscription'ları */
    private _secimSubs: Subscription[] = [];

    /** Seçili parçanın görüntülenen ismi */
    get seciliAktifParcaIsim(): string {
        if (!this.seciliAktifParca) return '(Seçilmedi)';
        return this.seciliAktifParca.name || this.seciliAktifParca.userData?.name || 'Parça';
    }

    // Maliyet detay gösterim state'leri
    maliyetDetayAcik = false;
    prosesKarsilastirmaAcik = true;

    // Custom item ekleme formu
    yeniCustomItem: { name: string; costPerPart: number; category: CustomCostItem['category'] } = {
        name: '', costPerPart: 0, category: 'other'
    };

    // Ayarlar
    ayarlar: DFMAyarlar = {
        otomatikAnaliz: false,
        secilenProses: 'injection_molding',
        raporFormati: 'html',
        gosterKritik: true,
        gosterUyari: true,
        gosterBilgi: true,
        gosterUygun: true,
        // Uretim parametreleri
        batchSize: 100,
        annualVolume: 1000,
        profitMarginPercent: 15,
        overheadPercent: 10,
        qualityControlPercent: 3,
        tightestToleranceMM: 0.1,
        requiredSurfaceRa: 3.2,
        secilenMalzeme: '',
        paraBirimi: 'USD',
        customCostItems: []
    };

    // Analyzer (public for template access)
    analyzer: FikoDFMAnalyzer;

    // Proses seçenekleri - Tüm 17 üretim süreci
    prosesSecenekleri: { value: ManufacturingProcess; label: string }[] = [
        // Döküm
        { value: 'die_casting', label: 'Basınçlı Döküm' },
        { value: 'sand_casting', label: 'Kum Döküm' },
        { value: 'investment_casting', label: 'Hassas Döküm' },
        { value: 'shell_mold_casting', label: 'Kabuk Kalıp Döküm' },
        { value: 'centrifugal_casting', label: 'Santrifüj Döküm' },
        // CNC
        { value: 'cnc_machining', label: 'CNC 3-Eksen' },
        { value: 'cnc_5axis', label: 'CNC 5-Eksen' },
        { value: 'cnc_turning', label: 'CNC Torna' },
        { value: 'cnc_edm', label: 'Tel Erozyon (EDM)' },
        { value: 'cnc_grinding', label: 'Taşlama' },
        // Plastik & Sac
        { value: 'injection_molding', label: 'Enjeksiyon Kalıplama' },
        { value: 'sheet_metal', label: 'Sac Metal' },
        // 3D Baskı & Dövme
        { value: '3d_printing', label: '3D Baskı' },
        { value: 'forging', label: 'Dövme' },
        // İşlem
        { value: 'welding', label: 'Kaynak' },
        { value: 'heat_treatment', label: 'Isıl İşlem' },
        { value: 'surface_treatment', label: 'Yüzey İşlem' }
    ];

    // Skor gösterge satırları (Sonuç sekmesinde detaylı bar'lar)
    scoreItems: { key: string; label: string }[] = [
        { key: 'processGeometryFit', label: 'Proses Uyumu' },
        { key: 'wallThickness', label: 'Duvar Kalınlığı' },
        { key: 'draftAngles', label: 'Çıkış Açısı' },
        { key: 'undercuts', label: 'Undercut' },
        { key: 'holes', label: 'Delikler' },
        { key: 'fillets', label: 'Fillet' },
        { key: 'ribs', label: 'Nervür' },
        { key: 'pockets', label: 'Cep' },
        { key: 'bosses', label: 'Boss' },
        { key: 'surfaceFinish', label: 'Yüzey Kalite' },
        { key: 'tolerances', label: 'Tolerans' },
        { key: 'threads', label: 'Diş' },
        { key: 'symmetry', label: 'Simetri' }
    ];

    constructor(public CD: ChangeDetectorRef) {
        this.analyzer = getDFMAnalyzer();
        this.detectGPUMode();
    }

    /**
     * Component init - seçili çizimleri otomatik al, ayarları yükle
     */
    ngOnInit(): void {
        // LocalStorage'dan ayarları yükle
        try {
            const kayitli = localStorage.getItem('fikoyt-dfm-ayarlar');
            if (kayitli) {
                const parsed = JSON.parse(kayitli);
                this.ayarlar = { ...this.ayarlar, ...parsed };
                this.analyzer.setProcess(this.ayarlar.secilenProses);
            }
        } catch (_) { /* ignore parse error */ }

        // Kısa bir gecikme ile seçimleri al (input binding'in tamamlanması için)
        setTimeout(() => {
            // Mevcut seçimden seciliAktifParca ayarla
            this.mevcutSecimiAl();
        }, 100);
    }

    /**
     * Seçili FikoCizimYalin'leri al (ürün ağacı veya sahneden)
     * Önce sahneler[0].secimler, sonra secililer dener
     * Seçimler secilenObjelerMap, liste veya her ikisinde olabilir
     */
    getSeciliCizimler(): SeciliCizimBilgi[] {
        this.seciliCizimler = [];
        this.hataMesaji = null;

        if (!this.anaGosterim) return this.seciliCizimler;

        // Önce kumpas pattern: sahneler[0].secimler dene
        let secililer = this.anaGosterim?.sahneler?.[0]?.secimler;

        // Fallback: anaGosterim.secililer dene
        if (!secililer) {
            secililer = this.anaGosterim?.secililer;
        }

        console.log('[FikoDFM] getSeciliCizimler:', {
            secililer,
            liste: secililer?.liste,
            secilenObjelerMap: secililer?.secilenObjelerMap
        });

        // Seçimleri topla - hem liste hem secilenObjelerMap'i kontrol et
        const secilenObjeler: any[] = [];

        // 1. liste'den al
        if (secililer?.liste && Array.isArray(secililer.liste)) {
            secilenObjeler.push(...secililer.liste);
        }

        // 2. secilenObjelerMap'ten al (asıl seçimler burada!)
        if (secililer?.secilenObjelerMap && secililer.secilenObjelerMap instanceof Map) {
            secililer.secilenObjelerMap.forEach((value: any, key: string) => {
                console.log('[FikoDFM] secilenObjelerMap item:', key, value);
                // value.object veya value direkt obje olabilir
                const obj = value?.object || value?.mesh || value;
                if (obj && !secilenObjeler.includes(obj)) {
                    secilenObjeler.push(obj);
                }
            });
        }

        console.log('[FikoDFM] Toplanan seçim sayısı:', secilenObjeler.length);

        // FikoCizimYalin'leri bul
        for (const item of secilenObjeler) {
            console.log('[FikoDFM] İşlenen item:', item?.type, item?.name);

            // FikoCizimYalin tipinde mi kontrol et
            if (item && item.type === 'FikoCizimYalin') {
                const cizim = item as FikoCizimYalin;
                const ad = cizim.name || cizim.userData?.name || 'Parça';
                if (!this.seciliCizimler.some(s => s.cizim === cizim)) {
                    this.seciliCizimler.push({ cizim, ad });
                }
            }
            // Eğer doğrudan FikoCizimYalin değilse, parent zincirinde ara
            else if (item) {
                let hedef = item;
                let derinlik = 0;
                while (hedef && hedef.type !== 'FikoCizimYalin' && derinlik < 20) {
                    console.log('[FikoDFM] Parent aranıyor:', hedef?.type, '->', hedef?.parent?.type);
                    hedef = hedef.parent;
                    derinlik++;
                }
                if (hedef && hedef.type === 'FikoCizimYalin') {
                    const cizim = hedef as FikoCizimYalin;
                    const ad = cizim.name || cizim.userData?.name || 'Parça';
                    // Aynı parça zaten eklenmemişse ekle
                    if (!this.seciliCizimler.some(s => s.cizim === cizim)) {
                        this.seciliCizimler.push({ cizim, ad });
                        console.log('[FikoDFM] FikoCizimYalin bulundu:', ad);
                    }
                }
            }
        }

        console.log('[FikoDFM] Bulunan FikoCizimYalin sayısı:', this.seciliCizimler.length);
        this.CD.detectChanges();
        return this.seciliCizimler;
    }

    /**
     * Seçili parçayı analiz et (gerçek veri ile)
     * seciliAktifParca kullanılır (parça seçim modu ile seçilen)
     * Async chunked processing ile UI kilitlenmeden çalışır
     */
    async seciliParcayiAnalizEt(): Promise<void> {
        this.hataMesaji = null;
        this.progressPercent = 0;
        this.progressMessage = 'Hazırlanıyor...';

        // Önce seciliAktifParca kontrol et, yoksa seciliCizimler'den al
        let hedefParca: FikoCizimYalin | null = this.seciliAktifParca;
        let parcaAdi: string;

        if (!hedefParca) {
            // Fallback: seciliCizimler'den al
            this.getSeciliCizimler();
            if (this.seciliCizimler.length > 0) {
                hedefParca = this.seciliCizimler[0].cizim;
                parcaAdi = this.seciliCizimler[0].ad;
            }
        } else {
            parcaAdi = this.seciliAktifParcaIsim;
        }

        if (!hedefParca) {
            this.hataMesaji = 'Lütfen önce "Seç" butonu ile bir parça seçin';
            this.progressPercent = 0;
            this.progressMessage = '';
            this.CD.detectChanges();
            return;
        }

        // meshGeom yoksa hata ver
        if (!hedefParca.meshGeom) {
            this.hataMesaji = 'Parçanın mesh geometrisi bulunamadı (meshGeom yok)';
            this.progressPercent = 0;
            this.progressMessage = '';
            this.CD.detectChanges();
            return;
        }

        this.isAnalyzing = true;
        this.progressPercent = 5;
        this.progressMessage = 'BREP çıkarılıyor...';
        this.CD.detectChanges();

        // Yield to UI thread
        await this.yieldToUI();

        try {
            this.progressPercent = 10;
            this.progressMessage = 'Geometri analiz ediliyor...';
            this.CD.detectChanges();
            await this.yieldToUI();

            // FikoCizimYalin'in grupBrepAnaliz metodunu kullan
            const brepResults = await hedefParca.grupBrepAnaliz({
                recognizeSurfaces: true,
                detectFeatures: true,
                computeCurvature: true
            });

            if (brepResults && brepResults.length > 0) {
                this.progressPercent = 50;
                this.progressMessage = 'DFM analizi yapılıyor...';
                this.CD.detectChanges();
                await this.yieldToUI();

                // İlk grup sonucunu al
                const targetResult = brepResults[0];

                // Gerçek analiz yap
                await this.analizBaslatAsync(targetResult, parcaAdi!);
            } else {
                this.hataMesaji = 'BREP analizi başarısız oldu - geometry bulunamadı';
                this.progressPercent = 0;
                this.progressMessage = '';
            }
        } catch (err) {
            console.error('[FikoDFM] BREP çıkarma hatası:', err);
            this.hataMesaji = 'Analiz sırasında hata oluştu: ' + (err as Error).message;
            this.progressPercent = 0;
            this.progressMessage = '';
        } finally {
            this.isAnalyzing = false;
            this.CD.detectChanges();
        }
    }

    /**
     * UI thread'e kontrol ver - UI kilitlenmesini önler
     */
    private yieldToUI(): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, 0));
    }

    /**
     * GPU/WASM modu tespit et
     */
    private detectGPUMode(): void {
        // WebGPU kontrolü
        if ('gpu' in navigator) {
            this.gpuMode = 'WEBGPU';
        } else if (typeof WebAssembly !== 'undefined') {
            this.gpuMode = 'WASM';
        } else {
            this.gpuMode = 'CPU';
        }
    }

    /**
     * DFM Analizi başlat (legacy - senkron)
     * @param brepResult - GrupBrepResult from FikoGPUBrepExtractor
     * @param parcaAdi - Part name for report
     * @param malzeme - Optional material
     */
    /** Ayarlardan DFMCostParams olustur */
    private buildCostParams(): DFMCostParams {
        return {
            batchSize: this.ayarlar.batchSize,
            annualVolume: this.ayarlar.annualVolume,
            profitMarginPercent: this.ayarlar.profitMarginPercent,
            overheadPercent: this.ayarlar.overheadPercent,
            qualityControlPercent: this.ayarlar.qualityControlPercent,
            tightestToleranceMM: this.ayarlar.tightestToleranceMM,
            requiredSurfaceRa: this.ayarlar.requiredSurfaceRa,
            materialId: this.ayarlar.secilenMalzeme || undefined,
            outputCurrency: this.ayarlar.paraBirimi,
            customCostItems: this.ayarlar.customCostItems.filter(c => c.enabled),
        };
    }

    async analizBaslat(brepResult: GrupBrepResult, parcaAdi: string = 'Parça', malzeme?: string): Promise<void> {
        if (this.isAnalyzing) return;

        this.isAnalyzing = true;
        this.hataMesaji = null;
        this.CD.detectChanges();

        try {
            this.analyzer.setProcess(this.ayarlar.secilenProses);
            this.currentReport = await this.analyzer.analyze(brepResult, parcaAdi, malzeme, undefined, this.buildCostParams());
            this._currentBrepResult = brepResult;
            this.dfmViewMode = 'Sonuc';
        } catch (err) {
            this.hataMesaji = 'DFM analizi başarısız: ' + (err as Error).message;
        } finally {
            this.isAnalyzing = false;
            this.CD.detectChanges();
        }
    }

    /**
     * DFM Analizi başlat - Async/Chunked (UI kilitlemez)
     * Progress göstergesi ile çalışır
     */
    async analizBaslatAsync(brepResult: GrupBrepResult, parcaAdi: string = 'Parça', malzeme?: string): Promise<void> {
        this.hataMesaji = null;

        try {
            this.progressPercent = 55;
            this.progressMessage = 'Proses ayarlanıyor...';
            this.CD.detectChanges();
            await this.yieldToUI();

            this.analyzer.setProcess(this.ayarlar.secilenProses);

            this.progressPercent = 60;
            this.progressMessage = 'Yüzeyler analiz ediliyor...';
            this.CD.detectChanges();
            await this.yieldToUI();

            // Chunked analiz - büyük veri için
            this.currentReport = await this.analyzer.analyze(brepResult, parcaAdi, malzeme, undefined, this.buildCostParams());

            this.progressPercent = 90;
            this.progressMessage = 'Sonuçlar hazırlanıyor...';
            this.CD.detectChanges();
            await this.yieldToUI();

            // BREP verisini sakla (vurgulama için)
            this._currentBrepResult = brepResult;

            this.progressPercent = 100;
            this.progressMessage = 'Tamamlandı!';
            this.dfmViewMode = 'Sonuc';
            this.CD.detectChanges();

            // Kısa bir süre sonra progress'i temizle
            setTimeout(() => {
                this.progressPercent = 0;
                this.progressMessage = '';
                this.CD.detectChanges();
            }, 1000);

        } catch (err) {
            console.error('[FikoDFM] Analiz hatası:', err);
            this.hataMesaji = 'DFM analizi başarısız: ' + (err as Error).message;
            this.progressPercent = 0;
            this.progressMessage = '';
            this.CD.detectChanges();
        }
    }

    /**
     * Proses değiştiğinde mevcut BREP verisiyle yeniden analiz et
     * Bu sayede kullanıcı proses değiştirince skor ve maliyet güncellenir
     */
    async prosesDegisenAnaliz(yeniProses: ManufacturingProcess): Promise<void> {
        this.ayarlar.secilenProses = yeniProses;
        this.analyzer.setProcess(yeniProses);

        // Eğer daha önce analiz yapılmışsa, saklanan BREP verisiyle yeniden analiz et
        if (this._currentBrepResult && !this.isAnalyzing) {
            this.isAnalyzing = true;
            this.CD.detectChanges();
            try {
                const parcaAdi = this.seciliAktifParcaIsim;
                await this.analizBaslatAsync(this._currentBrepResult, parcaAdi);
            } finally {
                this.isAnalyzing = false;
                this.CD.detectChanges();
            }
        } else {
            this.CD.detectChanges();
        }
    }

    /**
     * Rapor temizle
     */
    raporTemizle(): void {
        this.currentReport = null;
        this.selectedIssue = null;
        this.hataMesaji = null;
        this._currentBrepResult = null;
        this.clearHighlight();
        this.CD.detectChanges();
    }

    // ============ PARÇA SEÇİM MODLARI (Subscription Pattern) ============

    /**
     * Parça seçim modunu aç/kapat
     */
    parcaSecModuToggle(): void {
        if (this.parcaSecModu) {
            this.parcaSecModuKapat();
        } else {
            this.parcaSecModuAc();
        }
    }

    /** Önceki seçim modu durumu (seçim modunu kapatırken geri yüklemek için) */
    private _oncekiSecimModu: boolean | null = null;

    /**
     * Parça seçim modunu aç - secilenObjelerMap'i izle
     * tiklandi event'i güvenilir değil, bu yüzden secilenObjelerMap'i polling ile kontrol ediyoruz
     */
    parcaSecModuAc(): void {
        // Önce kumpas pattern: sahneler[0].secimler dene
        let secililer = this.anaGosterim?.sahneler?.[0]?.secimler;

        // Fallback: anaGosterim.secililer dene
        if (!secililer) {
            console.log('[FikoDFM] sahneler[0].secimler yok, anaGosterim.secililer deneniyor...');
            secililer = this.anaGosterim?.secililer;
        }

        if (!secililer) {
            this.hataMesaji = 'Seçim sistemi bulunamadı';
            console.warn('[FikoDFM] Seçim sistemi bulunamadı');
            this.CD.detectChanges();
            return;
        }

        // ============ SEÇİM SİSTEMİNİ AKTİF ET ============
        // Önceki durumu kaydet
        this._oncekiSecimModu = secililer.aktif ?? false;

        // Seçim sistemini aktif et (mode: "YOK" -> aktif)
        if (secililer.aktif !== undefined) {
            secililer.aktif = true;
            console.log('[FikoDFM] Seçim sistemi aktif edildi');
        }

        // Mevcut seçimleri temizle (yeni seçim için)
        if (secililer.secilenObjelerMap) {
            secililer.secilenObjelerMap.clear();
            console.log('[FikoDFM] Mevcut seçimler temizlendi');
        }

        this.parcaSecModu = true;
        this.hataMesaji = null;
        console.log('[FikoDFM] Parça seçim modu açıldı - seçim bekleniyor...', {
            secililer,
            aktif: secililer.aktif,
            mode: secililer.mode
        });

        // Mevcut seçimleri temizle (önceki seçimleri almamak için Map'in boyutunu kaydet)
        const baslangicSecimSayisi = secililer?.secilenObjelerMap?.size || 0;

        // secilenObjelerMap'i polling ile izle (100ms aralıklarla)
        const pollInterval = setInterval(() => {
            if (!this.parcaSecModu) {
                clearInterval(pollInterval);
                return;
            }

            const mevcutSecimSayisi = secililer?.secilenObjelerMap?.size || 0;
            console.log('[FikoDFM] Polling - seçim sayısı:', mevcutSecimSayisi, 'başlangıç:', baslangicSecimSayisi);

            // Yeni seçim yapılmış mı?
            if (mevcutSecimSayisi > 0) {
                // secilenObjelerMap'ten FikoCizimYalin bul
                let bulunanCizim: FikoCizimYalin | null = null;

                secililer.secilenObjelerMap.forEach((value: any, _key: string) => {
                    if (bulunanCizim) return; // Zaten bulduk

                    const obj = value?.object || value?.mesh || value;
                    if (!obj) return;

                    console.log('[FikoDFM] Seçilen obje kontrol ediliyor:', obj?.type, obj?.name);

                    // FikoCizimYalin'i bul
                    let hedef = obj;
                    let derinlik = 0;
                    while (hedef && hedef.type !== 'FikoCizimYalin' && derinlik < 20) {
                        hedef = hedef.parent;
                        derinlik++;
                    }

                    if (hedef && hedef.type === 'FikoCizimYalin') {
                        bulunanCizim = hedef as FikoCizimYalin;
                    }
                });

                if (bulunanCizim) {
                    this.seciliAktifParca = bulunanCizim;
                    console.log('[FikoDFM] FikoCizimYalin BULUNDU:', this.seciliAktifParcaIsim);
                    clearInterval(pollInterval);
                    this.parcaSecModuKapat();
                }
            }
        }, 100);

        // Timeout - 30 saniye sonra otomatik kapat
        setTimeout(() => {
            if (this.parcaSecModu) {
                clearInterval(pollInterval);
                this.hataMesaji = 'Seçim zaman aşımına uğradı';
                this.parcaSecModuKapat();
            }
        }, 30000);

        // Ayrıca tiklandi event'ine de subscribe ol (belki çalışır)
        if (secililer?.tiklandi) {
            const sub = secililer.tiklandi.subscribe((kesisim: any) => {
                console.log('[FikoDFM] tiklandi event alındı:', kesisim);

                if (!kesisim?.object) return;

                let hedef = kesisim.object;
                let derinlik = 0;
                while (hedef && hedef.type !== 'FikoCizimYalin' && derinlik < 20) {
                    hedef = hedef.parent;
                    derinlik++;
                }

                if (hedef && hedef.type === 'FikoCizimYalin') {
                    this.seciliAktifParca = hedef as FikoCizimYalin;
                    console.log('[FikoDFM] FikoCizimYalin BULUNDU (tiklandi):', this.seciliAktifParcaIsim);
                    clearInterval(pollInterval);
                    this.parcaSecModuKapat();
                }
            });
            this._secimSubs.push(sub);
        }

        this.CD.detectChanges();
    }

    /**
     * Parça seçim modunu kapat - subscription'ları temizle ve önceki durumu geri yükle
     */
    parcaSecModuKapat(): void {
        this.parcaSecModu = false;

        // Önceki seçim durumunu geri yükle
        const secililer = this.anaGosterim?.sahneler?.[0]?.secimler || this.anaGosterim?.secililer;
        if (secililer && this._oncekiSecimModu !== null) {
            secililer.aktif = this._oncekiSecimModu;
            console.log('[FikoDFM] Seçim sistemi önceki duruma döndürüldü:', this._oncekiSecimModu);
            this._oncekiSecimModu = null;
        }

        // Subscription'ları temizle
        this._secimSubs.forEach(s => s.unsubscribe());
        this._secimSubs = [];

        this.CD.detectChanges();
    }

    /**
     * Seçili parçayı temizle
     */
    seciliParcayiTemizle(): void {
        this.seciliAktifParca = null;
        this.CD.detectChanges();
    }

    /**
     * Sahnedeki mevcut seçimi al ve seciliAktifParca olarak ayarla
     */
    mevcutSecimiAl(): void {
        this.getSeciliCizimler();
        this.hataMesaji = null;

        if (this.seciliCizimler.length > 0) {
            this.seciliAktifParca = this.seciliCizimler[0].cizim;
            console.log('[FikoDFM] Mevcut seçim alındı:', this.seciliAktifParcaIsim);
        } else {
            // Hata mesajı gösterme - sessizce başarısız ol
        }

        this.CD.detectChanges();
    }

    // ============ CUSTOM COST ITEM CRUD ============

    /** Custom maliyet kalemi ekle */
    customItemEkle(): void {
        if (!this.yeniCustomItem.name || this.yeniCustomItem.costPerPart <= 0) return;

        const item: CustomCostItem = {
            id: `ci_${Date.now().toString(36)}`,
            name: this.yeniCustomItem.name,
            costPerPart: this.yeniCustomItem.costPerPart,
            category: this.yeniCustomItem.category,
            enabled: true,
        };
        this.ayarlar.customCostItems.push(item);
        this.yeniCustomItem = { name: '', costPerPart: 0, category: 'other' };
        this.kaydetAyarlar(this.ayarlar);
    }

    /** Custom maliyet kalemi sil */
    customItemSil(id: string): void {
        this.ayarlar.customCostItems = this.ayarlar.customCostItems.filter(c => c.id !== id);
        this.kaydetAyarlar(this.ayarlar);
    }

    /** Custom maliyet kalemi toggle */
    customItemToggle(id: string): void {
        const item = this.ayarlar.customCostItems.find(c => c.id === id);
        if (item) {
            item.enabled = !item.enabled;
            this.kaydetAyarlar(this.ayarlar);
        }
    }

    /** Hizli preset ekle */
    customPresetEkle(preset: 'ambalaj' | 'nakliye' | 'montaj' | 'test'): void {
        const presets: Record<string, { name: string; cost: number; cat: CustomCostItem['category'] }> = {
            ambalaj: { name: 'Ambalaj', cost: 0.50, cat: 'packaging' },
            nakliye: { name: 'Nakliye', cost: 1.00, cat: 'shipping' },
            montaj: { name: 'Montaj Iscilik', cost: 2.00, cat: 'assembly' },
            test: { name: 'Test/Sertifika', cost: 0.75, cat: 'testing' },
        };
        const p = presets[preset];
        if (!p) return;
        // Ayni isimde varsa ekleme
        if (this.ayarlar.customCostItems.some(c => c.name === p.name)) return;
        this.ayarlar.customCostItems.push({
            id: `ci_${Date.now().toString(36)}`,
            name: p.name,
            costPerPart: p.cost,
            category: p.cat,
            enabled: true,
        });
        this.kaydetAyarlar(this.ayarlar);
    }

    /** Custom items toplam */
    get customItemsToplam(): number {
        return this.ayarlar.customCostItems
            .filter(c => c.enabled)
            .reduce((sum, c) => sum + c.costPerPart, 0);
    }

    /** Para birimi simgesi */
    get paraBirimiSimge(): string {
        switch (this.ayarlar.paraBirimi) {
            case 'USD': return '$';
            case 'EUR': return '\u20AC';
            case 'TRY': return '\u20BA';
            default: return '$';
        }
    }

    /** Custom item kategori etiketi */
    getCategoryLabel(cat: string): string {
        const labels: Record<string, string> = {
            packaging: 'Ambalaj', shipping: 'Nakliye', assembly: 'Montaj',
            testing: 'Test', certification: 'Sertifika', other: 'Diger'
        };
        return labels[cat] || cat;
    }

    /** Proses ismi (TR) */
    getProsesLabel(processName: string): string {
        const found = this.prosesSecenekleri.find(p => p.value === processName);
        return found?.label || processName;
    }

    /**
     * Component temizliği - Memory cleanup
     */
    ngOnDestroy(): void {
        // Seçim modunu kapat ve subscription'ları temizle
        this.parcaSecModuKapat();

        // Highlight'ları temizle
        this.clearHighlight();

        // BREP referansını temizle
        this._currentBrepResult = null;

        // Raporu temizle
        this.currentReport = null;
        this.selectedIssue = null;

        // Progress state'i temizle
        this.progressPercent = 0;
        this.progressMessage = '';

        // Parça referansını temizle
        this.seciliAktifParca = null;
        this.seciliCizimler = [];
    }

    /**
     * Issue seçildiğinde
     */
    onIssueSelected(issue: DFMIssue): void {
        this.selectedIssue = issue;
        this.issueSelected.emit(issue);
        this.CD.detectChanges();
    }

    /**
     * Raporu indir
     */
    raporIndir(format: 'txt' | 'html' | 'json' = 'html'): void {
        if (!this.currentReport) return;

        let content: string;
        let mimeType: string;
        let extension: string;

        switch (format) {
            case 'txt':
                content = this.analyzer.formatTextReport(this.currentReport);
                mimeType = 'text/plain';
                extension = 'txt';
                break;
            case 'json':
                content = this.analyzer.formatJSONReport(this.currentReport);
                mimeType = 'application/json';
                extension = 'json';
                break;
            case 'html':
            default:
                content = this.analyzer.formatHTMLReport(this.currentReport);
                mimeType = 'text/html';
                extension = 'html';
        }

        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DFM_Rapor_${this.currentReport.partName}_${this.currentReport.reportId}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Ayarları kaydet
     */
    kaydetAyarlar(yeniAyarlar: DFMAyarlar): void {
        this.ayarlar = { ...yeniAyarlar };
        this.analyzer.setProcess(this.ayarlar.secilenProses);
        localStorage.setItem('fikoyt-dfm-ayarlar', JSON.stringify(this.ayarlar));
        this.CD.detectChanges();
    }

    /**
     * Kapat
     */
    kapa(): void {
        this.kapandiEvent.emit();
    }

    /**
     * Severity rengi (Starfield HUD palette)
     */
    getSeverityColor(severity: DFMSeverity): string {
        switch (severity) {
            case 'critical': return '#ff5252';
            case 'warning': return '#ffab00';
            case 'info': return '#00e5ff';
            case 'pass': return '#00e676';
        }
    }

    /**
     * Severity ikonu
     */
    getSeverityIcon(severity: DFMSeverity): string {
        switch (severity) {
            case 'critical': return 'close circle';
            case 'warning': return 'warning sign';
            case 'info': return 'info circle';
            case 'pass': return 'check circle';
        }
    }

    /**
     * Skor rengi (Starfield HUD gradient)
     */
    getScoreColor(score: number): string {
        if (score >= 80) return '#00e676';
        if (score >= 60) return '#4ecdc4';
        if (score >= 40) return '#ffab00';
        return '#ff5252';
    }

    /**
     * Rating metni (Türkçe)
     */
    getRatingText(rating: string): string {
        switch (rating) {
            case 'excellent': return 'MUKEMMEL';
            case 'good': return 'IYI';
            case 'acceptable': return 'KABUL';
            case 'poor': return 'ZAYIF';
            case 'unmanufacturable': return 'URETILEMEZ';
            default: return rating?.toUpperCase() || '';
        }
    }

    // ============ YÜZEY VURGULAMA SİSTEMİ ============

    /**
     * Etkilenen yüzeye tıklandığında - kalıcı vurgulama
     */
    onFeatureClick(featureId: string): void {
        console.log('[FikoDFM] Feature tıklandı:', featureId);

        // Aynı feature'a tekrar tıklanırsa vurgulamayı kaldır
        if (this.highlightedFeature === featureId) {
            this.clearHighlight();
            return;
        }

        // Yeni feature'ı vurgula
        this.highlightFeatureInScene(featureId, true);
        this.highlightedFeature = featureId;
        this.CD.detectChanges();
    }

    /**
     * Etkilenen yüzeyin üzerine gelindiğinde - geçici vurgulama
     */
    onFeatureHover(featureId: string | null): void {
        // Kalıcı vurgulama varsa hover'ı yoksay
        if (this.highlightedFeature) return;

        if (featureId) {
            this.highlightFeatureInScene(featureId, false);
        } else {
            this.clearHighlight();
        }
    }

    /**
     * Sahnede yüzeyi vurgula
     * @param featureId - F37, E12 gibi yüzey/edge ID'si
     * @param permanent - Kalıcı mı geçici mi
     */
    private highlightFeatureInScene(featureId: string, permanent: boolean): void {
        if (!this.seciliAktifParca || !this.anaGosterim) {
            console.warn('[FikoDFM] Vurgulama için parça veya sahne bulunamadı');
            return;
        }

        // Feature ID'sinden tip ve index'i çıkar (F37 -> Face 37, E12 -> Edge 12)
        const match = featureId.match(/^([FEV])(\d+)$/);
        if (!match) {
            console.warn('[FikoDFM] Geçersiz feature ID:', featureId);
            return;
        }

        const featureType = match[1]; // F, E, V
        const featureIndex = parseInt(match[2], 10);

        console.log('[FikoDFM] Vurgulama:', featureType, featureIndex, permanent ? '(kalıcı)' : '(geçici)');

        try {
            // Önceki vurgulamayı temizle
            this.restoreOriginalMaterials();

            // Parçanın mesh'ini bul
            const mesh = this.findMeshInPart(this.seciliAktifParca);
            if (!mesh) {
                console.warn('[FikoDFM] Parçada mesh bulunamadı');
                return;
            }

            // Feature tipine göre vurgula
            if (featureType === 'F') {
                this.highlightFace(mesh, featureIndex, permanent);
            } else if (featureType === 'E') {
                this.highlightEdge(mesh, featureIndex, permanent);
            } else if (featureType === 'V') {
                this.highlightVertex(mesh, featureIndex, permanent);
            }

            // Render'ı tetikle
            if (this.anaGosterim.render) {
                this.anaGosterim.render();
            }
        } catch (err) {
            console.error('[FikoDFM] Vurgulama hatası:', err);
        }
    }

    /**
     * Parçada mesh bul - FikoCizimYalin yapısına göre
     */
    private findMeshInPart(part: any): any {
        console.log('[FikoDFM] findMeshInPart:', part?.type, part?.name, 'isMesh:', part?.isMesh);

        // Doğrudan mesh ise
        if (part.isMesh) {
            console.log('[FikoDFM] Doğrudan mesh bulundu');
            return part;
        }

        // FikoCizimYalin'in meshGeom'u BufferGeometry, mesh'i children'da
        // Önce children'da Mesh ara
        if (part.children && part.children.length > 0) {
            for (const child of part.children) {
                if (child.isMesh) {
                    console.log('[FikoDFM] Child mesh bulundu:', child.name || child.type);
                    return child;
                }
            }
            // Derinlemesine ara
            for (const child of part.children) {
                const mesh = this.findMeshInPart(child);
                if (mesh) return mesh;
            }
        }

        // meshGeom varsa ve bu geometry'yi kullanan bir mesh oluştur
        if (part.meshGeom && part.meshGeom.isBufferGeometry) {
            console.log('[FikoDFM] meshGeom BufferGeometry bulundu, geometry kullanılacak');
            // Geometry'yi döndür, createFaceOverlay bunu kullanacak
            return { geometry: part.meshGeom, isVirtualMesh: true, parent: part };
        }

        console.log('[FikoDFM] Mesh bulunamadı');
        return null;
    }

    /**
     * Yüzeyi (Face) vurgula - BREP triangleIndices kullan
     * BREP analizi Region Growing ile kendi face gruplarını oluşturur
     * Bu gruplar geometry'deki 'grup' attribute ile AYNI DEĞİL!
     */
    private highlightFace(mesh: any, faceIndex: number, permanent: boolean): void {
        const geometry = mesh.geometry;
        if (!geometry) {
            console.warn('[FikoDFM] Mesh geometry yok');
            return;
        }

        const positions = geometry.getAttribute('position');

        console.log('[FikoDFM] Face vurgulama başlıyor - faceIndex:', faceIndex,
            'positions:', positions?.count);

        // BREP'ten face'in triangle index'lerini al (Region Growing ile oluşturulmuş)
        const faceId = `F${faceIndex}`;
        let triangleIndices: number[] | null = null;

        if (this._currentBrepResult?.brep?.faces) {
            const brepFace = this._currentBrepResult.brep.faces.get(faceId);
            console.log('[FikoDFM] BREP face aranıyor:', faceId, 'bulundu:', !!brepFace);

            if (brepFace) {
                // triangleIndices varsa kullan
                if ((brepFace as any).triangleIndices) {
                    triangleIndices = (brepFace as any).triangleIndices;
                    console.log('[FikoDFM] BREP triangleIndices bulundu:', triangleIndices?.length, 'üçgen');
                } else {
                    // triangleIndices yoksa log
                    console.log('[FikoDFM] triangleIndices yok, surface type:', brepFace.surface?.type);
                }
            }

            // Mevcut face ID'lerini logla (debug)
            if (!brepFace) {
                const existingFaces = Array.from(this._currentBrepResult.brep.faces.keys()).slice(0, 20);
                console.log('[FikoDFM] Mevcut BREP face IDleri:', existingFaces);
            }
        }

        if (triangleIndices && triangleIndices.length > 0) {
            this.createFaceOverlay(mesh, triangleIndices, permanent);
        } else {
            console.warn('[FikoDFM] Face için triangleIndices bulunamadı, faceId:', faceId);
            // Fallback denemesi: geometry grup attribute
            const grupAttr = geometry.getAttribute('grup');
            if (grupAttr) {
                triangleIndices = this.findTrianglesByGrupAttr(geometry, faceIndex);
                if (triangleIndices && triangleIndices.length > 0) {
                    console.log('[FikoDFM] Fallback: grup attr ile', triangleIndices.length, 'üçgen bulundu');
                    this.createFaceOverlay(mesh, triangleIndices, permanent);
                }
            }
        }
    }

    /**
     * Grup attribute'undan face'e ait üçgenleri bul
     * Grup attribute formatları:
     * - itemSize=3: vec3(startIdx, count, groupId) -> Z bileşeni = groupId
     * - itemSize=1: doğrudan groupId
     * - itemSize=4: vec4(..., groupId) -> W bileşeni = groupId
     */
    private findTrianglesByGrupAttr(geometry: THREE.BufferGeometry, faceIndex: number): number[] {
        const grupAttr = geometry.getAttribute('grup');
        const indices = geometry.getIndex();
        const positions = geometry.getAttribute('position');

        if (!grupAttr || !positions) return [];

        const itemSize = grupAttr.itemSize;
        const triangleCount = indices ? indices.count / 3 : positions.count / 3;
        const matchingTriangles: number[] = [];

        // İlk birkaç üçgenin grup değerlerini logla (debug)
        const uniqueGroups = new Set<number>();
        const sampleSize = Math.min(50, triangleCount);

        for (let tri = 0; tri < sampleSize; tri++) {
            const vertIdx = indices ? indices.getX(tri * 3) : tri * 3;
            let groupId: number;

            if (itemSize === 1) {
                groupId = grupAttr.getX(vertIdx);
            } else if (itemSize === 3) {
                // Z bileşeni = groupId
                groupId = grupAttr.getZ(vertIdx);
            } else if (itemSize === 4) {
                // W bileşeni = groupId
                groupId = grupAttr.getW(vertIdx);
            } else {
                // Fallback: son bileşen
                groupId = grupAttr.array[vertIdx * itemSize + (itemSize - 1)];
            }

            uniqueGroups.add(Math.floor(groupId));
        }

        console.log('[FikoDFM] Grup attr analizi - itemSize:', itemSize,
            'triangleCount:', triangleCount,
            'aranan faceIndex:', faceIndex,
            'mevcut gruplar (ilk', sampleSize, '):', Array.from(uniqueGroups).sort((a, b) => a - b).slice(0, 20));

        // Tüm üçgenleri tara
        for (let tri = 0; tri < triangleCount; tri++) {
            const vertIdx = indices ? indices.getX(tri * 3) : tri * 3;
            let groupId: number;

            if (itemSize === 1) {
                groupId = grupAttr.getX(vertIdx);
            } else if (itemSize === 3) {
                groupId = grupAttr.getZ(vertIdx);
            } else if (itemSize === 4) {
                groupId = grupAttr.getW(vertIdx);
            } else {
                groupId = grupAttr.array[vertIdx * itemSize + (itemSize - 1)];
            }

            if (Math.floor(groupId) === faceIndex) {
                matchingTriangles.push(tri);
            }
        }

        console.log('[FikoDFM] Face', faceIndex, 'için', matchingTriangles.length, 'üçgen bulundu');

        // Eğer bulunamadıysa, en yakın grup ID'yi dene (offset sorunu olabilir)
        if (matchingTriangles.length === 0) {
            // Grup ID'leri 1-indexed olabilir
            const altFaceIndex = faceIndex + 1;
            for (let tri = 0; tri < triangleCount; tri++) {
                const vertIdx = indices ? indices.getX(tri * 3) : tri * 3;
                let groupId: number;

                if (itemSize === 1) {
                    groupId = grupAttr.getX(vertIdx);
                } else if (itemSize === 3) {
                    groupId = grupAttr.getZ(vertIdx);
                } else if (itemSize === 4) {
                    groupId = grupAttr.getW(vertIdx);
                } else {
                    groupId = grupAttr.array[vertIdx * itemSize + (itemSize - 1)];
                }

                if (Math.floor(groupId) === altFaceIndex) {
                    matchingTriangles.push(tri);
                }
            }

            if (matchingTriangles.length > 0) {
                console.log('[FikoDFM] 1-indexed offset ile', matchingTriangles.length, 'üçgen bulundu (faceIndex+1)');
            }
        }

        return matchingTriangles;
    }

    /**
     * Face overlay mesh oluştur - doğrudan geometry'den üçgenleri kopyala
     */
    private createFaceOverlay(mesh: any, triangleIndices: number[], permanent: boolean): void {
        // Virtual mesh durumu (sadece geometry + parent)
        const isVirtual = mesh.isVirtualMesh === true;
        const geometry = mesh.geometry;
        const parentObj = isVirtual ? mesh.parent : mesh;

        if (!geometry) {
            console.warn('[FikoDFM] Geometry yok');
            return;
        }

        const positions = geometry.getAttribute('position');
        const normals = geometry.getAttribute('normal');
        const indices = geometry.getIndex();

        if (!positions) {
            console.warn('[FikoDFM] Positions attribute yok');
            return;
        }

        console.log('[FikoDFM] createFaceOverlay - triangles:', triangleIndices.length,
            'positions:', positions.count, 'indexed:', !!indices, 'isVirtual:', isVirtual);

        // Overlay için yeni geometry oluştur
        const newPositions: number[] = [];
        const newNormals: number[] = [];

        for (const triIdx of triangleIndices) {
            for (let v = 0; v < 3; v++) {
                const vertIdx = indices ? indices.getX(triIdx * 3 + v) : triIdx * 3 + v;

                // Bounds check
                if (vertIdx >= positions.count) {
                    console.warn('[FikoDFM] vertIdx out of bounds:', vertIdx, '>=', positions.count);
                    continue;
                }

                newPositions.push(
                    positions.getX(vertIdx),
                    positions.getY(vertIdx),
                    positions.getZ(vertIdx)
                );

                if (normals && vertIdx < normals.count) {
                    newNormals.push(
                        normals.getX(vertIdx),
                        normals.getY(vertIdx),
                        normals.getZ(vertIdx)
                    );
                }
            }
        }

        if (newPositions.length === 0) {
            console.warn('[FikoDFM] Overlay için vertex bulunamadı');
            return;
        }

        // Overlay geometry
        const overlayGeom = new THREE.BufferGeometry();
        overlayGeom.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
        if (newNormals.length > 0) {
            overlayGeom.setAttribute('normal', new THREE.Float32BufferAttribute(newNormals, 3));
        }
        overlayGeom.computeBoundingSphere();

        // Overlay material - parlak sarı, hafif şeffaf
        const overlayMat = new THREE.MeshBasicMaterial({
            color: this._highlightColor,
            transparent: true,
            opacity: permanent ? 0.75 : 0.5,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2
        });

        // Overlay mesh
        const overlayMesh = new THREE.Mesh(overlayGeom, overlayMat);
        overlayMesh.name = '__dfm_face_highlight__';
        overlayMesh.renderOrder = 999;

        // Parent'a child olarak ekle
        if (parentObj && parentObj.add) {
            parentObj.add(overlayMesh);
            console.log('[FikoDFM] Overlay parent\'a eklendi:', parentObj.name || parentObj.type);
        } else {
            // Fallback: sahneye ekle
            const scene = this.anaGosterim?.sahneler?.[0];
            if (scene) {
                scene.add(overlayMesh);
                console.log('[FikoDFM] Overlay sahneye eklendi (fallback)');
            }
        }

        this._originalMaterials.set('highlightMesh', overlayMesh);

        console.log('[FikoDFM] Face overlay oluşturuldu:', triangleIndices.length, 'üçgen,',
            newPositions.length / 3, 'vertex');
    }

    /**
     * Edge vurgulama
     */
    private highlightEdge(mesh: any, edgeIndex: number, permanent: boolean): void {
        // Edge'leri bul (LineSegments olarak)
        const parent = mesh.parent;
        if (!parent) return;

        // Parent'ta edge mesh ara
        for (const child of parent.children) {
            if (child.isLineSegments || child.type === 'LineSegments') {
                console.log('[FikoDFM] Edge mesh bulundu, vurgulama yapılıyor');

                // Edge rengini değiştir
                if (child.material) {
                    this._originalMaterials.set('edgeColor', child.material.color?.getHex() || 0x000000);
                    child.material.color?.setHex(this._highlightColor);
                    child.material.linewidth = permanent ? 3 : 2;
                    child.material.needsUpdate = true;
                }
                break;
            }
        }
    }

    /**
     * Vertex vurgulama
     */
    private highlightVertex(mesh: any, vertexIndex: number, permanent: boolean): void {
        // Vertex vurgulama için nokta oluştur
        console.log('[FikoDFM] Vertex vurgulama:', vertexIndex);

        const geometry = mesh.geometry;
        const position = geometry?.getAttribute('position');
        if (!position || vertexIndex >= position.count) return;

        // Vertex pozisyonunu al
        const x = position.getX(vertexIndex);
        const y = position.getY(vertexIndex);
        const z = position.getZ(vertexIndex);

        // Küçük bir sphere oluştur
        const sphereGeom = new THREE.SphereGeometry(0.5, 8, 8);
        const sphereMat = new THREE.MeshBasicMaterial({
            color: this._highlightColor,
            transparent: true,
            opacity: permanent ? 0.9 : 0.6
        });
        const sphere = new THREE.Mesh(sphereGeom, sphereMat);
        sphere.position.set(x, y, z);
        sphere.name = '__dfm_vertex_highlight__';

        // World matrix uygula
        mesh.updateMatrixWorld(true);
        sphere.applyMatrix4(mesh.matrixWorld);

        // Sahneye ekle
        const scene = this.anaGosterim?.sahneler?.[0];
        if (scene) {
            scene.add(sphere);
            this._originalMaterials.set('vertexSphere', sphere);
        }
    }

    /**
     * Orijinal materyalleri geri yükle
     */
    private restoreOriginalMaterials(): void {
        // Vertex colors'ı geri yükle
        if (this._originalMaterials.has('colors') && this.seciliAktifParca) {
            const mesh = this.findMeshInPart(this.seciliAktifParca);
            if (mesh?.geometry) {
                const colors = mesh.geometry.getAttribute('color');
                if (colors) {
                    const originalColors = this._originalMaterials.get('colors');
                    (colors.array as Float32Array).set(originalColors);
                    colors.needsUpdate = true;
                }
            }
        }

        // Vertex colors flag'ini geri yükle
        if (this._originalMaterials.has('vertexColors') && this.seciliAktifParca) {
            const mesh = this.findMeshInPart(this.seciliAktifParca);
            if (mesh?.material) {
                mesh.material.vertexColors = this._originalMaterials.get('vertexColors');
                mesh.material.needsUpdate = true;
            }
        }

        // Edge rengini geri yükle
        if (this._originalMaterials.has('edgeColor') && this.seciliAktifParca) {
            const mesh = this.findMeshInPart(this.seciliAktifParca);
            const parent = mesh?.parent;
            if (parent) {
                for (const child of parent.children) {
                    if (child.isLineSegments) {
                        child.material?.color?.setHex(this._originalMaterials.get('edgeColor'));
                        child.material.linewidth = 1;
                        child.material.needsUpdate = true;
                    }
                }
            }
        }

        // Highlight mesh'i kaldır (face vurgulama için)
        if (this._originalMaterials.has('highlightMesh')) {
            const highlightMesh = this._originalMaterials.get('highlightMesh');
            highlightMesh.parent?.remove(highlightMesh);
            highlightMesh.geometry?.dispose();
            highlightMesh.material?.dispose();
            console.log('[FikoDFM] Highlight mesh kaldırıldı');
        }

        // Vertex sphere'i kaldır
        if (this._originalMaterials.has('vertexSphere')) {
            const sphere = this._originalMaterials.get('vertexSphere');
            sphere.parent?.remove(sphere);
            sphere.geometry?.dispose();
            sphere.material?.dispose();
        }

        // Face material'larını geri yükle
        for (const [key, value] of this._originalMaterials) {
            if (typeof key === 'number' && this.seciliAktifParca) {
                const mesh = this.findMeshInPart(this.seciliAktifParca);
                if (mesh?.material && Array.isArray(mesh.material)) {
                    mesh.material[key] = value;
                }
            }
        }

        this._originalMaterials.clear();
    }

    /**
     * Vurgulamayı temizle
     */
    clearHighlight(): void {
        this.restoreOriginalMaterials();
        this.highlightedFeature = null;

        // Render'ı tetikle
        if (this.anaGosterim?.render) {
            this.anaGosterim.render();
        }

        this.CD.detectChanges();
    }

    trackByValue(index: number, item: any): any {
        return item?.value ?? item?.label ?? index;
    }

    trackByIndex(index: number): number {
        return index;
    }

    trackByCategory(index: number, item: any): string {
        return item?.category ?? item?.title ?? index;
    }
}
